import { useState, useEffect } from 'react';
import { 
    Plus, 
    Truck as TruckIcon,
    Edit,
    Trash2,
    Phone,
    User,
    Percent,
    MapPin,
    Route,
    ChevronRight,
    DollarSign,
    Clock,
    TrendingUp,
    ArrowRight,
    X,
    Loader2,
    AlertTriangle,
    Ban,
    Home,
    Target
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { usePendingConfirmations } from '../contexts/PendingConfirmationsContext';
import HOSStatusCard from '../components/HOSStatusCard';
import ChainOptimizerPanel from '../components/ChainOptimizerPanel';
import OptimizerStatusPanel from '../components/OptimizerStatusPanel';
import ComplianceTab from '../components/ComplianceTab';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Shield } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const US_STATES = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const TRAILER_TYPES = ['van', 'reefer', 'flatbed', 'power_only'];

const LOAD_METHOD_OPTIONS = [
    { value: 'none', label: '--' },  // 'none' = no restriction (maps to '' in backend)
    { value: 'live_load', label: 'Live Load' },
    { value: 'drop_and_hook', label: 'Drop & Hook' }
];

const HOME_TIME_PREFERENCES = [
    { value: '1', label: '1 Reset' },
    { value: '2', label: '2 Resets' },
    { value: '3', label: '3 Resets' },
    { value: '4', label: '4 Resets' }
];

export default function TruckProfilesPage() {
    const [trucks, setTrucks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDialog, setShowDialog] = useState(false);
    const [editingTruck, setEditingTruck] = useState(null);
    const [saving, setSaving] = useState(false);
    const [complianceStatus, setComplianceStatus] = useState({}); // {truck_id: {compliant: bool, failures: []}}
    
    // Pending confirmations context for badge updates
    const { refresh: refreshPendingCount } = usePendingConfirmations();
    
    // Tri-Haul Chain Generation State
    const [showChainsDialog, setShowChainsDialog] = useState(false);
    const [generatingChains, setGeneratingChains] = useState(false);
    const [selectedTruckForChains, setSelectedTruckForChains] = useState(null);
    const [generatedChains, setGeneratedChains] = useState([]);
    const [chainGenerationResult, setChainGenerationResult] = useState(null);
    const [selectedChainDetail, setSelectedChainDetail] = useState(null);
    
    // Chain Staging State
    const [stagingChain, setStagingChain] = useState(false);
    const [stageResult, setStageResult] = useState(null);

    const [formData, setFormData] = useState({
        name: '',
        driver_name: '',
        driver_phone: '',
        trailer_type: 'van',
        load_method: 'none',  // 'none' = no restriction (maps to '' in backend)
        commission_percent: 10,
        // Current Location (GPS or manual)
        current_city: '',
        current_state: '',
        // Home Base & No-Go States
        home_base_city: '',
        home_base_state: '',
        no_go_states: [],
        // Trailer Specifications
        trailer_length: 53,
        max_weight: 45000,
        search_radius_miles: 150,
        // Home Time Preference
        home_time_preference: '2',
        // Load Strategy (Required)
        days_out_target: 5,
        max_deadhead_miles: 250,
        heat_radius_miles: 150,
        // Revenue Targets
        weekly_target_revenue: 6500,
        daily_target_revenue: 1000,
        min_weekly_floor: 5000,
        // Preferred Lanes
        preferred_lanes: [
            { origin_city: '', origin_state: '', destination_city: '', destination_state: '' },
            { origin_city: '', origin_state: '', destination_city: '', destination_state: '' },
            { origin_city: '', origin_state: '', destination_city: '', destination_state: '' }
        ]
    });
    
    // Form validation - Load Strategy fields are required
    const isFormValid = () => {
        return formData.name && 
               formData.days_out_target && 
               formData.max_deadhead_miles && 
               formData.heat_radius_miles;
    };

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const trucksRes = await fetch(`${API_URL}/api/trucks`, { credentials: 'include' });

            if (trucksRes.ok) {
                const data = await trucksRes.json();
                setTrucks(data);
                
                // Fetch compliance status for each truck in parallel
                const compliancePromises = data.map(truck => 
                    fetch(`${API_URL}/api/trucks/${truck.truck_id}/compliance`, { credentials: 'include' })
                        .then(res => res.ok ? res.json() : null)
                        .catch(() => null)
                );
                const complianceResults = await Promise.allSettled(compliancePromises);
                const statusMap = {};
                data.forEach((truck, idx) => {
                    const result = complianceResults[idx];
                    if (result.status === 'fulfilled' && result.value) {
                        statusMap[truck.truck_id] = result.value;
                    }
                });
                setComplianceStatus(statusMap);
            }
        } catch (error) {
            console.error('Failed to fetch data:', error);
            toast.error('Failed to load truck profiles');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!formData.name) {
            toast.error('Truck name is required');
            return;
        }

        setSaving(true);
        try {
            const url = editingTruck 
                ? `${API_URL}/api/trucks/${editingTruck.truck_id}`
                : `${API_URL}/api/trucks`;
            
            // Prepare payload with proper types
            const payload = {
                ...formData,
                max_deadhead_miles: formData.max_deadhead_miles ? parseFloat(formData.max_deadhead_miles) : null,
                // Map 'none' back to '' for backend (no restriction)
                load_method: formData.load_method === 'none' ? '' : formData.load_method
            };
            
            const response = await fetch(url, {
                method: editingTruck ? 'PATCH' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                toast.success(editingTruck ? 'Truck updated!' : 'Truck created!');
                setShowDialog(false);
                setEditingTruck(null);
                resetForm();
                fetchData();
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Failed to save truck');
            }
        } catch (error) {
            console.error('Failed to save:', error);
            toast.error('Failed to save truck');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (truckId) => {
        if (!window.confirm('Delete this truck profile?')) return;

        try {
            const response = await fetch(`${API_URL}/api/trucks/${truckId}`, {
                method: 'DELETE',
                credentials: 'include'
            });

            if (response.ok) {
                toast.success('Truck deleted');
                fetchData();
            } else {
                toast.error('Failed to delete truck');
            }
        } catch (error) {
            console.error('Failed to delete:', error);
            toast.error('Failed to delete truck');
        }
    };

    const openEditDialog = (truck) => {
        setEditingTruck(truck);
        setFormData({
            name: truck.name,
            driver_name: truck.driver_name || '',
            driver_phone: truck.driver_phone || '',
            trailer_type: truck.trailer_type || 'van',
            load_method: truck.load_method || 'none',  // Map '' from backend to 'none'
            commission_percent: truck.commission_percent,
            current_city: truck.current_city || '',
            current_state: truck.current_state || '',
            home_base_city: truck.home_base_city || '',
            home_base_state: truck.home_base_state || '',
            no_go_states: truck.no_go_states || [],
            // Trailer Specifications
            trailer_length: truck.trailer_length || 53,
            max_weight: truck.max_weight || 45000,
            search_radius_miles: truck.search_radius_miles || 150,
            // Home Time Preference
            home_time_preference: truck.home_time_preference || '2',
            // Load Strategy
            days_out_target: truck.days_out_target || 5,
            max_deadhead_miles: truck.max_deadhead_miles || 250,
            heat_radius_miles: truck.heat_radius_miles || 150,
            // Revenue Targets
            weekly_target_revenue: truck.weekly_target_revenue || 6500,
            daily_target_revenue: truck.daily_target_revenue || 1000,
            min_weekly_floor: truck.min_weekly_floor || 5000,
            // Preferred Lanes
            preferred_lanes: truck.preferred_lanes || [
                { origin_city: '', origin_state: '', destination_city: '', destination_state: '' },
                { origin_city: '', origin_state: '', destination_city: '', destination_state: '' },
                { origin_city: '', origin_state: '', destination_city: '', destination_state: '' }
            ]
        });
        setShowDialog(true);
    };

    const resetForm = () => {
        setFormData({
            name: '',
            driver_name: '',
            driver_phone: '',
            trailer_type: 'van',
            load_method: 'none',  // 'none' = no restriction (default)
            commission_percent: 10,
            current_city: '',
            current_state: '',
            home_base_city: '',
            home_base_state: '',
            no_go_states: [],
            // Trailer Specifications
            trailer_length: 53,
            max_weight: 45000,
            search_radius_miles: 150,
            // Home Time Preference
            home_time_preference: '2',
            // Load Strategy defaults
            days_out_target: 5,
            max_deadhead_miles: 250,
            heat_radius_miles: 150,
            // Revenue Targets defaults
            weekly_target_revenue: 6500,
            daily_target_revenue: 1000,
            min_weekly_floor: 5000,
            // Preferred Lanes
            preferred_lanes: [
                { origin_city: '', origin_state: '', destination_city: '', destination_state: '' },
                { origin_city: '', origin_state: '', destination_city: '', destination_state: '' },
                { origin_city: '', origin_state: '', destination_city: '', destination_state: '' }
            ]
        });
    };
    
    const toggleNoGoState = (state) => {
        const current = formData.no_go_states;
        if (current.includes(state)) {
            setFormData({ ...formData, no_go_states: current.filter(s => s !== state) });
        } else {
            setFormData({ ...formData, no_go_states: [...current, state].sort() });
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            available: 'status-active',
            on_load: 'status-in-transit',
            off_duty: 'status-pending'
        };
        return colors[status] || 'status-pending';
    };

    // Generate Optimal Chains for a truck
    const handleGenerateChains = async (truck) => {
        setSelectedTruckForChains(truck);
        setGeneratedChains([]);
        setChainGenerationResult(null);
        setSelectedChainDetail(null);
        setShowChainsDialog(true);
        setGeneratingChains(true);

        try {
            const response = await fetch(`${API_URL}/api/load-chains/generate/${truck.truck_id}`, {
                method: 'POST',
                credentials: 'include'
            });

            const data = await response.json();

            if (response.ok) {
                setGeneratedChains(data.chains || []);
                setChainGenerationResult(data);
                if (data.chains?.length > 0) {
                    toast.success(`Generated ${data.chains.length} optimal chains!`);
                } else {
                    toast.info(data.message || 'No chains could be generated');
                }
            } else {
                toast.error(data.detail || 'Failed to generate chains');
                setChainGenerationResult({ error: data.detail });
            }
        } catch (error) {
            console.error('Failed to generate chains:', error);
            toast.error('Failed to generate chains');
            setChainGenerationResult({ error: 'Network error' });
        } finally {
            setGeneratingChains(false);
        }
    };

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    // Format number with decimals
    const formatNumber = (num, decimals = 1) => {
        return Number(num).toFixed(decimals);
    };

    // Stage Chain - assigns loads to truck with pending confirmation status
    const handleStageChain = async (chain) => {
        if (!chain?.chain_id) {
            toast.error('Invalid chain');
            return;
        }

        setStagingChain(true);
        setStageResult(null);

        try {
            const response = await fetch(`${API_URL}/api/load-chains/stage`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ chain_id: chain.chain_id })
            });

            const data = await response.json();

            if (response.ok) {
                setStageResult(data);
                // Refresh the pending confirmations badge count
                refreshPendingCount();
                if (data.failed_loads?.length > 0) {
                    toast.warning(`Chain partially staged. ${data.staged_loads.length} loads staged, ${data.failed_loads.length} failed.`);
                } else {
                    toast.success(`Chain staged successfully! ${data.staged_loads.length} loads assigned to ${data.truck_name}.`);
                }
            } else {
                toast.error(data.detail || 'Failed to stage chain');
                setStageResult({ error: data.detail });
            }
        } catch (error) {
            console.error('Failed to stage chain:', error);
            toast.error('Failed to stage chain');
            setStageResult({ error: 'Network error' });
        } finally {
            setStagingChain(false);
        }
    };

    return (
        <div className="space-y-6 animate-fade-in" data-testid="truck-profiles-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-white tracking-tight uppercase">
                        Truck Profiles
                    </h1>
                    <p className="text-zinc-500 text-sm mt-1">
                        Configure 5 trucks with commission rates and lane assignments
                    </p>
                </div>
                <Button 
                    onClick={() => { resetForm(); setEditingTruck(null); setShowDialog(true); }}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    data-testid="add-truck-btn"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Truck
                </Button>
            </div>

            {/* Trucks Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {loading ? (
                    <div className="col-span-full flex items-center justify-center py-12">
                        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : trucks.length === 0 ? (
                    <div className="col-span-full text-center py-12 text-zinc-500">
                        <TruckIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>No truck profiles yet</p>
                    </div>
                ) : (
                    trucks.map((truck) => (
                        <Card 
                            key={truck.truck_id}
                            className="bg-zinc-900/50 border-zinc-800 hover:border-zinc-700 transition-colors"
                            data-testid={`truck-card-${truck.truck_id}`}
                        >
                            <CardContent className="p-5">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-orange-500/20 rounded-sm flex items-center justify-center">
                                            <TruckIcon className="w-5 h-5 text-orange-500" />
                                        </div>
                                        <div>
                                            <h3 className="text-white font-semibold">{truck.name}</h3>
                                            <Badge className={`${getStatusColor(truck.status)} text-xs mt-1`}>
                                                {truck.status?.replace('_', ' ')}
                                            </Badge>
                                        </div>
                                    </div>
                                    <div className="flex gap-1">
                                        <Button 
                                            variant="ghost" 
                                            size="icon" 
                                            className="h-8 w-8 text-zinc-400 hover:text-white"
                                            onClick={() => openEditDialog(truck)}
                                        >
                                            <Edit className="w-4 h-4" />
                                        </Button>
                                        <Button 
                                            variant="ghost" 
                                            size="icon" 
                                            className="h-8 w-8 text-zinc-400 hover:text-red-400"
                                            onClick={() => handleDelete(truck.truck_id)}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    {truck.driver_name && (
                                        <div className="flex items-center gap-2 text-sm text-zinc-400">
                                            <User className="w-4 h-4" />
                                            {truck.driver_name}
                                        </div>
                                    )}
                                    {truck.driver_phone && (
                                        <div className="flex items-center gap-2 text-sm text-zinc-400">
                                            <Phone className="w-4 h-4" />
                                            {truck.driver_phone}
                                        </div>
                                    )}
                                    <div className="flex items-center gap-2 text-sm text-zinc-400">
                                        <TruckIcon className="w-4 h-4" />
                                        {truck.trailer_type === 'power_only' ? 'POWER ONLY' : truck.trailer_type?.toUpperCase() || 'VAN'}
                                        {truck.load_method && (
                                            <span className="text-zinc-500">
                                                • {truck.load_method === 'live_load' ? 'Live Only' : 'Drop & Hook Only'}
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2 text-sm">
                                        <Percent className="w-4 h-4 text-zinc-400" />
                                        <span className="text-orange-400 font-mono font-semibold">
                                            {truck.commission_percent}% commission
                                        </span>
                                    </div>
                                    
                                    {/* Compliance Status Indicator */}
                                    {complianceStatus[truck.truck_id] && (
                                        <div 
                                            className={`flex items-center gap-2 text-sm cursor-pointer hover:opacity-80`}
                                            onClick={() => openEditDialog(truck)}
                                            data-testid={`compliance-badge-${truck.truck_id}`}
                                        >
                                            {complianceStatus[truck.truck_id].compliant ? (
                                                <>
                                                    <Shield className="w-4 h-4 text-green-400" />
                                                    <span className="text-green-400 font-medium">DOT Compliant</span>
                                                </>
                                            ) : (
                                                <>
                                                    <AlertTriangle className="w-4 h-4 text-red-400" />
                                                    <span className="text-red-400 font-medium">
                                                        {complianceStatus[truck.truck_id].failures?.length || 0} Compliance Issue(s)
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* HOS Status Section */}
                                <div className="mt-4 pt-4 border-t border-zinc-800">
                                    <HOSStatusCard truckId={truck.truck_id} onRefresh={fetchData} autoRefresh={true} />
                                </div>

                                {/* Optimizer Status Telemetry (Phase 5.1) */}
                                <div className="mt-4 pt-4 border-t border-zinc-800">
                                    <OptimizerStatusPanel truckId={truck.truck_id} />
                                </div>

                                {/* Chain Optimizer */}
                                <div className="mt-4 pt-4 border-t border-zinc-800">
                                    <ChainOptimizerPanel 
                                        truckId={truck.truck_id} 
                                        onSelectChain={(chain) => {
                                            toast.info(`Selected chain: ${chain.load_ids.join(' → ')}`);
                                        }}
                                    />
                                </div>
                            </CardContent>
                        </Card>
                    ))
                )}
            </div>

            {/* Add/Edit Truck Dialog */}
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] flex flex-col">
                    <DialogHeader className="shrink-0">
                        <DialogTitle className="text-white">
                            {editingTruck ? 'Edit Truck' : 'Add Truck'}
                        </DialogTitle>
                        <DialogDescription className="text-zinc-400">
                            Configure truck profile, commission, and compliance
                        </DialogDescription>
                    </DialogHeader>
                    
                    <Tabs defaultValue="profile" className="flex-1 flex flex-col min-h-0">
                        <TabsList className="shrink-0 bg-zinc-800 border-zinc-700 w-full justify-start">
                            <TabsTrigger value="profile" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
                                <User className="w-4 h-4 mr-2" />
                                Profile
                            </TabsTrigger>
                            {editingTruck && (
                                <TabsTrigger value="compliance" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
                                    <Shield className="w-4 h-4 mr-2" />
                                    Safety Compliance
                                </TabsTrigger>
                            )}
                        </TabsList>
                        
                        <TabsContent value="profile" className="flex-1 flex flex-col min-h-0 mt-0">
                    <form 
                        onSubmit={(e) => { e.preventDefault(); handleSave(); }}
                        className="flex flex-col flex-1 min-h-0"
                    >
                        <div className="flex-1 overflow-y-auto space-y-4 py-4 px-1">
                            <div>
                                <Label className="text-zinc-400">Truck Name *</Label>
                                <Input
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="bg-zinc-800 border-zinc-700 mt-1"
                                    placeholder="Truck 1 - John's Peterbilt"
                                    data-testid="truck-name-input"
                                    required
                                />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="text-zinc-400">Driver Name</Label>
                                <Input
                                    value={formData.driver_name}
                                    onChange={(e) => setFormData({ ...formData, driver_name: e.target.value })}
                                    className="bg-zinc-800 border-zinc-700 mt-1"
                                    placeholder="John Doe"
                                />
                            </div>
                            <div>
                                <Label className="text-zinc-400">Driver Phone</Label>
                                <Input
                                    value={formData.driver_phone}
                                    onChange={(e) => setFormData({ ...formData, driver_phone: e.target.value })}
                                    className="bg-zinc-800 border-zinc-700 mt-1"
                                    placeholder="(555) 123-4567"
                                />
                            </div>
                        </div>

                        <div>
                            <Label className="text-zinc-400">Trailer Type</Label>
                            <Select
                                value={formData.trailer_type}
                                onValueChange={(value) => setFormData({ ...formData, trailer_type: value })}
                            >
                                <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-900 border-zinc-800">
                                    {TRAILER_TYPES.map(type => (
                                        <SelectItem key={type} value={type}>
                                            {type === 'power_only' ? 'Power Only' : type.charAt(0).toUpperCase() + type.slice(1)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Current Location - GPS Override */}
                        <div className="pt-2 border-t border-zinc-800">
                            <Label className="text-zinc-400 flex items-center gap-2 mb-2">
                                <MapPin className="w-4 h-4 text-cyan-400" />
                                Current Location
                                <span className="text-xs text-zinc-600 ml-1">(Override GPS)</span>
                            </Label>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Input
                                        value={formData.current_city}
                                        onChange={(e) => setFormData({ ...formData, current_city: e.target.value })}
                                        className="bg-zinc-800 border-zinc-700"
                                        placeholder="City"
                                    />
                                </div>
                                <div>
                                    <Select
                                        value={formData.current_state}
                                        onValueChange={(value) => setFormData({ ...formData, current_state: value })}
                                    >
                                        <SelectTrigger className="bg-zinc-800 border-zinc-700">
                                            <SelectValue placeholder="State" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-zinc-900 border-zinc-800 max-h-60">
                                            {US_STATES.map(state => (
                                                <SelectItem key={state} value={state}>{state}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <p className="text-xs text-zinc-500 mt-1">
                                Set truck's current location. Leave blank to use GPS.
                            </p>
                        </div>

                        {/* Home Base */}
                        <div className="pt-2 border-t border-zinc-800">
                            <Label className="text-zinc-400 flex items-center gap-2 mb-2">
                                <Home className="w-4 h-4 text-blue-400" />
                                Home Base
                            </Label>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Input
                                        value={formData.home_base_city}
                                        onChange={(e) => setFormData({ ...formData, home_base_city: e.target.value })}
                                        className="bg-zinc-800 border-zinc-700"
                                        placeholder="City"
                                    />
                                </div>
                                <div>
                                    <Select
                                        value={formData.home_base_state}
                                        onValueChange={(value) => setFormData({ ...formData, home_base_state: value })}
                                    >
                                        <SelectTrigger className="bg-zinc-800 border-zinc-700">
                                            <SelectValue placeholder="State" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-zinc-900 border-zinc-800 max-h-60">
                                            {US_STATES.map(state => (
                                                <SelectItem key={state} value={state}>{state}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </div>

                        {/* Trailer Specifications */}
                        <div className="pt-2 border-t border-zinc-800">
                            <Label className="text-zinc-400 flex items-center gap-2 mb-2">
                                <TruckIcon className="w-4 h-4 text-purple-400" />
                                Trailer Specifications
                            </Label>
                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <Label className="text-xs text-zinc-500 mb-1">Length (ft)</Label>
                                    <Select
                                        value={String(formData.trailer_length)}
                                        onValueChange={(value) => setFormData({ ...formData, trailer_length: parseInt(value) })}
                                    >
                                        <SelectTrigger className="bg-zinc-800 border-zinc-700">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-zinc-900 border-zinc-800">
                                            <SelectItem value="48">48 ft</SelectItem>
                                            <SelectItem value="53">53 ft</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="text-xs text-zinc-500 mb-1">Max Weight (lbs)</Label>
                                    <Select
                                        value={String(formData.max_weight)}
                                        onValueChange={(value) => setFormData({ ...formData, max_weight: parseInt(value) })}
                                    >
                                        <SelectTrigger className="bg-zinc-800 border-zinc-700">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-zinc-900 border-zinc-800">
                                            <SelectItem value="40000">40,000 lbs</SelectItem>
                                            <SelectItem value="42000">42,000 lbs</SelectItem>
                                            <SelectItem value="44000">44,000 lbs</SelectItem>
                                            <SelectItem value="45000">45,000 lbs</SelectItem>
                                            <SelectItem value="48000">48,000 lbs</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="text-xs text-zinc-500 mb-1">Search Radius (mi)</Label>
                                    <Select
                                        value={String(formData.search_radius_miles)}
                                        onValueChange={(value) => setFormData({ ...formData, search_radius_miles: parseFloat(value) })}
                                    >
                                        <SelectTrigger className="bg-zinc-800 border-zinc-700">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-zinc-900 border-zinc-800">
                                            <SelectItem value="50">50 mi</SelectItem>
                                            <SelectItem value="100">100 mi</SelectItem>
                                            <SelectItem value="150">150 mi</SelectItem>
                                            <SelectItem value="200">200 mi</SelectItem>
                                            <SelectItem value="250">250 mi</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <p className="text-xs text-zinc-500 mt-2">
                                Loads outside these limits will be hidden from search results.
                            </p>
                        </div>

                        {/* Home Time Preference */}
                        <div className="pt-2 border-t border-zinc-800">
                            <Label className="text-zinc-400 flex items-center gap-2 mb-2">
                                <Home className="w-4 h-4 text-green-400" />
                                Home Time Preference
                            </Label>
                            <p className="text-xs text-zinc-500 mb-2">
                                How often the truck should return toward home base based on reset cycles
                            </p>
                            <Select
                                value={formData.home_time_preference}
                                onValueChange={(value) => setFormData({ ...formData, home_time_preference: value })}
                            >
                                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                                    <SelectValue placeholder="Select resets" />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-900 border-zinc-800">
                                    {HOME_TIME_PREFERENCES.map(opt => (
                                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Load Method */}
                        <div className="pt-2 border-t border-zinc-800">
                            <Label className="text-zinc-400 flex items-center gap-2 mb-2">
                                <TruckIcon className="w-4 h-4 text-blue-400" />
                                Load Method
                            </Label>
                            <p className="text-xs text-zinc-500 mb-2">
                                Filter loads by handling type. "--" allows both Live Load and Drop & Hook.
                            </p>
                            <Select
                                value={formData.load_method}
                                onValueChange={(value) => setFormData({ ...formData, load_method: value })}
                            >
                                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                                    <SelectValue placeholder="--" />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-900 border-zinc-800">
                                    {LOAD_METHOD_OPTIONS.map(opt => (
                                        <SelectItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* No-Go States */}
                        <div className="pt-2 border-t border-zinc-800">
                            <Label className="text-zinc-400 flex items-center gap-2 mb-2">
                                <Ban className="w-4 h-4 text-red-400" />
                                No-Go States (Hard Block)
                            </Label>
                            <p className="text-xs text-zinc-500 mb-2">
                                Truck cannot be booked for loads with origin OR destination in these states.
                            </p>
                            <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto p-2 bg-zinc-800/50 rounded border border-zinc-700">
                                {US_STATES.map(state => {
                                    const isSelected = formData.no_go_states.includes(state);
                                    return (
                                        <Badge
                                            key={state}
                                            variant={isSelected ? "default" : "outline"}
                                            className={`cursor-pointer transition-colors ${
                                                isSelected 
                                                    ? 'bg-red-500 hover:bg-red-600 text-white' 
                                                    : 'border-zinc-600 text-zinc-400 hover:border-red-500 hover:text-red-400'
                                            }`}
                                            onClick={() => toggleNoGoState(state)}
                                        >
                                            {state}
                                        </Badge>
                                    );
                                })}
                            </div>
                            {formData.no_go_states.length > 0 && (
                                <p className="text-xs text-red-400 mt-2 flex items-center gap-1">
                                    <AlertTriangle className="w-3 h-3" />
                                    {formData.no_go_states.length} state(s) blocked: {formData.no_go_states.join(', ')}
                                </p>
                            )}
                        </div>

                        {/* LOAD STRATEGY Section */}
                        <div className="border-t border-zinc-700 pt-4 mt-2">
                            <Label className="text-orange-400 text-sm uppercase tracking-wider mb-3 block flex items-center gap-2">
                                <Route className="w-4 h-4" />
                                Load Strategy
                            </Label>
                            <p className="text-xs text-zinc-500 mb-4">
                                Configure chain generation parameters for this truck
                            </p>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-zinc-400">Days Out Target *</Label>
                                    <Input
                                        type="number"
                                        value={formData.days_out_target}
                                        onChange={(e) => setFormData({ ...formData, days_out_target: parseInt(e.target.value) || '' })}
                                        className="bg-zinc-800 border-zinc-700 mt-1"
                                        placeholder="5"
                                        min={1}
                                        max={14}
                                        required
                                    />
                                    <p className="text-xs text-zinc-500 mt-1">Days before returning home</p>
                                </div>

                                <div>
                                    <Label className="text-zinc-400">Max Deadhead Miles *</Label>
                                    <Input
                                        type="number"
                                        value={formData.max_deadhead_miles}
                                        onChange={(e) => setFormData({ ...formData, max_deadhead_miles: parseFloat(e.target.value) || '' })}
                                        className="bg-zinc-800 border-zinc-700 mt-1"
                                        placeholder="250"
                                        min={50}
                                        max={500}
                                        required
                                    />
                                    <p className="text-xs text-zinc-500 mt-1">Max empty miles to pickup</p>
                                </div>

                                <div>
                                    <Label className="text-zinc-400">Heat Radius Miles *</Label>
                                    <Input
                                        type="number"
                                        value={formData.heat_radius_miles}
                                        onChange={(e) => setFormData({ ...formData, heat_radius_miles: parseFloat(e.target.value) || '' })}
                                        className="bg-zinc-800 border-zinc-700 mt-1"
                                        placeholder="150"
                                        min={50}
                                        max={300}
                                        required
                                    />
                                    <p className="text-xs text-zinc-500 mt-1">Heat zone search radius</p>
                                </div>
                            </div>
                        </div>

                        {/* Preferred Lanes Section */}
                        <div className="border-t border-zinc-700 pt-4 mt-2">
                            <Label className="text-cyan-400 text-sm uppercase tracking-wider mb-3 block flex items-center gap-2">
                                <Route className="w-4 h-4" />
                                Preferred Lanes
                            </Label>
                            <p className="text-xs text-zinc-500 mb-4">
                                Up to 3 preferred freight lanes for dispatcher reference
                            </p>
                            
                            {formData.preferred_lanes.map((lane, idx) => (
                                <div key={idx} className="mb-4 p-3 bg-zinc-800/30 rounded-lg border border-zinc-700">
                                    <Label className="text-zinc-400 text-xs mb-2 block">Lane {idx + 1}</Label>
                                    <div className="grid grid-cols-2 gap-2 mb-2">
                                        <Input
                                            value={lane.origin_city}
                                            onChange={(e) => {
                                                const newLanes = [...formData.preferred_lanes];
                                                newLanes[idx].origin_city = e.target.value;
                                                setFormData({ ...formData, preferred_lanes: newLanes });
                                            }}
                                            className="bg-zinc-800 border-zinc-700 text-sm"
                                            placeholder="Origin City"
                                        />
                                        <Select
                                            value={lane.origin_state}
                                            onValueChange={(value) => {
                                                const newLanes = [...formData.preferred_lanes];
                                                newLanes[idx].origin_state = value;
                                                setFormData({ ...formData, preferred_lanes: newLanes });
                                            }}
                                        >
                                            <SelectTrigger className="bg-zinc-800 border-zinc-700 text-sm">
                                                <SelectValue placeholder="State" />
                                            </SelectTrigger>
                                            <SelectContent className="bg-zinc-900 border-zinc-800 max-h-48">
                                                {US_STATES.map(state => (
                                                    <SelectItem key={state} value={state}>{state}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="flex items-center justify-center my-1">
                                        <ArrowRight className="w-4 h-4 text-zinc-500" />
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <Input
                                            value={lane.destination_city}
                                            onChange={(e) => {
                                                const newLanes = [...formData.preferred_lanes];
                                                newLanes[idx].destination_city = e.target.value;
                                                setFormData({ ...formData, preferred_lanes: newLanes });
                                            }}
                                            className="bg-zinc-800 border-zinc-700 text-sm"
                                            placeholder="Destination City"
                                        />
                                        <Select
                                            value={lane.destination_state}
                                            onValueChange={(value) => {
                                                const newLanes = [...formData.preferred_lanes];
                                                newLanes[idx].destination_state = value;
                                                setFormData({ ...formData, preferred_lanes: newLanes });
                                            }}
                                        >
                                            <SelectTrigger className="bg-zinc-800 border-zinc-700 text-sm">
                                                <SelectValue placeholder="State" />
                                            </SelectTrigger>
                                            <SelectContent className="bg-zinc-900 border-zinc-800 max-h-48">
                                                {US_STATES.map(state => (
                                                    <SelectItem key={state} value={state}>{state}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div>
                            <Label className="text-zinc-400">
                                Commission: <span className="text-orange-400 font-mono">{formData.commission_percent}%</span>
                            </Label>
                            <Slider
                                value={[formData.commission_percent]}
                                onValueChange={([value]) => setFormData({ ...formData, commission_percent: value })}
                                min={0}
                                max={30}
                                step={0.5}
                                className="mt-3"
                            />
                        </div>

                        {/* Revenue Targets Section */}
                        <div className="border-t border-zinc-800 pt-4 mt-4">
                            <div className="flex items-center gap-2 mb-3">
                                <Target className="w-4 h-4 text-orange-400" />
                                <h3 className="text-white font-medium">Revenue Targets</h3>
                            </div>
                            <p className="text-xs text-zinc-500 mb-4">
                                Set weekly revenue goals for performance tracking
                            </p>
                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <Label className="text-zinc-400">Weekly Target ($)</Label>
                                    <Input
                                        type="number"
                                        value={formData.weekly_target_revenue}
                                        onChange={(e) => setFormData({ ...formData, weekly_target_revenue: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                                        onBlur={(e) => {
                                            if (e.target.value === '' || isNaN(parseFloat(e.target.value))) {
                                                setFormData({ ...formData, weekly_target_revenue: 6500 });
                                            }
                                        }}
                                        className="bg-zinc-800 border-zinc-700 mt-1"
                                        placeholder="6500"
                                        min={1000}
                                        max={20000}
                                    />
                                </div>

                                <div>
                                    <Label className="text-zinc-400">Daily Target ($)</Label>
                                    <Input
                                        type="number"
                                        value={formData.daily_target_revenue}
                                        onChange={(e) => setFormData({ ...formData, daily_target_revenue: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                                        onBlur={(e) => {
                                            if (e.target.value === '' || isNaN(parseFloat(e.target.value))) {
                                                setFormData({ ...formData, daily_target_revenue: 1000 });
                                            }
                                        }}
                                        className="bg-zinc-800 border-zinc-700 mt-1"
                                        placeholder="1000"
                                        min={500}
                                        max={5000}
                                    />
                                </div>

                                <div>
                                    <Label className="text-zinc-400">Min Weekly Floor ($)</Label>
                                    <Input
                                        type="number"
                                        value={formData.min_weekly_floor}
                                        onChange={(e) => setFormData({ ...formData, min_weekly_floor: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                                        onBlur={(e) => {
                                            if (e.target.value === '' || isNaN(parseFloat(e.target.value))) {
                                                setFormData({ ...formData, min_weekly_floor: 5000 });
                                            }
                                        }}
                                        className="bg-zinc-800 border-zinc-700 mt-1"
                                        placeholder="5000"
                                        min={1000}
                                        max={15000}
                                    />
                                    <p className="text-xs text-zinc-500 mt-1">Triggers "Critical" if below</p>
                                </div>
                            </div>
                        </div>
                        </div>

                        {/* Sticky Footer */}
                        <div className="shrink-0 flex justify-end gap-3 pt-4 border-t border-zinc-800 mt-4 bg-zinc-900">
                            <Button 
                                type="button"
                                variant="outline" 
                                onClick={() => setShowDialog(false)}
                                className="border-zinc-700"
                            >
                                Cancel
                            </Button>
                            <Button 
                                type="submit"
                                disabled={saving || !isFormValid()}
                                className="bg-orange-500 hover:bg-orange-600 text-white min-w-[100px] disabled:opacity-50"
                                data-testid="save-truck-btn"
                            >
                                {saving ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Saving...
                                    </>
                                ) : editingTruck ? 'Update Truck' : 'Create Truck'}
                            </Button>
                        </div>
                    </form>
                        </TabsContent>
                        
                        {editingTruck && (
                            <TabsContent value="compliance" className="flex-1 overflow-y-auto min-h-0 mt-0">
                                <ComplianceTab 
                                    truck={editingTruck} 
                                    onUpdate={() => {
                                        fetchData();
                                    }}
                                />
                            </TabsContent>
                        )}
                    </Tabs>
                </DialogContent>
            </Dialog>

            {/* Tri-Haul Chains Dialog */}
            <Dialog open={showChainsDialog} onOpenChange={setShowChainsDialog}>
                <DialogContent className="bg-zinc-900 border-zinc-800 max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                    <DialogHeader className="shrink-0">
                        <DialogTitle className="text-white flex items-center gap-2">
                            <Route className="w-5 h-5 text-orange-500" />
                            Optimal Tri-Haul Chains
                        </DialogTitle>
                        <DialogDescription className="text-zinc-400">
                            {selectedTruckForChains && (
                                <>
                                    Generated for <span className="text-white font-medium">{selectedTruckForChains.name}</span>
                                    {chainGenerationResult && !chainGenerationResult.error && (
                                        <span className="ml-2 text-zinc-500">
                                            • {chainGenerationResult.available_loads_count} loads available
                                            • {chainGenerationResult.combinations_evaluated} combinations evaluated
                                        </span>
                                    )}
                                </>
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="flex-1 overflow-y-auto py-4 min-h-0">
                        {generatingChains ? (
                            <div className="flex flex-col items-center justify-center py-12">
                                <Loader2 className="w-10 h-10 text-orange-500 animate-spin mb-4" />
                                <p className="text-zinc-400">Analyzing available loads...</p>
                                <p className="text-zinc-500 text-sm mt-1">Finding optimal 3-load combinations</p>
                            </div>
                        ) : chainGenerationResult?.error ? (
                            <div className="flex flex-col items-center justify-center py-12">
                                <AlertTriangle className="w-10 h-10 text-red-400 mb-4" />
                                <p className="text-red-400">{chainGenerationResult.error}</p>
                            </div>
                        ) : generatedChains.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-12">
                                <Route className="w-10 h-10 text-zinc-600 mb-4" />
                                <p className="text-zinc-400">No chains could be generated</p>
                                <p className="text-zinc-500 text-sm mt-1">
                                    Need at least 3 available loads within deadhead constraints
                                </p>
                            </div>
                        ) : selectedChainDetail ? (
                            // Chain Detail View
                            <div className="space-y-4">
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => {
                                        setSelectedChainDetail(null);
                                        setStageResult(null);
                                    }}
                                    className="text-zinc-400 hover:text-white mb-2"
                                >
                                    <ChevronRight className="w-4 h-4 mr-1 rotate-180" />
                                    Back to list
                                </Button>

                                <div className="grid grid-cols-4 gap-3 mb-6">
                                    <div className="bg-zinc-800/50 border border-zinc-700 rounded-sm p-3">
                                        <p className="text-xs text-zinc-500 uppercase">Total Revenue</p>
                                        <p className="text-xl font-mono font-bold text-green-400 mt-1">
                                            {formatCurrency(selectedChainDetail.total_revenue)}
                                        </p>
                                    </div>
                                    <div className="bg-zinc-800/50 border border-zinc-700 rounded-sm p-3">
                                        <p className="text-xs text-zinc-500 uppercase">Net Revenue</p>
                                        <p className="text-xl font-mono font-bold text-green-400 mt-1">
                                            {formatCurrency(selectedChainDetail.total_net_revenue)}
                                        </p>
                                    </div>
                                    <div className="bg-zinc-800/50 border border-zinc-700 rounded-sm p-3">
                                        <p className="text-xs text-zinc-500 uppercase">Est. Days</p>
                                        <p className="text-xl font-mono font-bold text-white mt-1">
                                            {formatNumber(selectedChainDetail.estimated_total_days)}
                                        </p>
                                    </div>
                                    <div className="bg-zinc-800/50 border border-orange-500/30 rounded-sm p-3">
                                        <p className="text-xs text-zinc-500 uppercase">Chain Score</p>
                                        <p className="text-xl font-mono font-bold text-orange-400 mt-1">
                                            {formatNumber(selectedChainDetail.chain_score, 0)}
                                        </p>
                                    </div>
                                </div>

                                {/* High Risk Broker Warning */}
                                {selectedChainDetail.high_risk_broker && (
                                    <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-4" data-testid="high-risk-broker-warning">
                                        <div className="flex items-start gap-3">
                                            <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                                            <div>
                                                <h4 className="text-sm font-medium text-red-400 mb-1">
                                                    High Risk Broker Chain
                                                </h4>
                                                <p className="text-xs text-red-400/80">
                                                    One or more loads in this chain have brokers that do not meet credit/payment standards 
                                                    (credit score &lt; 90 or days to pay &gt; 30). Staging this chain is not recommended.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="bg-zinc-800/30 border border-zinc-700 rounded-sm p-4">
                                    <h4 className="text-sm font-medium text-zinc-300 mb-4 uppercase tracking-wide">
                                        Load Sequence
                                    </h4>
                                    <div className="space-y-3">
                                        {selectedChainDetail.loads.map((load, idx) => (
                                            <div key={load.load_id} className="relative">
                                                {idx > 0 && (
                                                    <div className="absolute -top-3 left-6 text-xs text-zinc-500 bg-zinc-900 px-2">
                                                        {idx === 1 
                                                            ? `${formatNumber(selectedChainDetail.deadhead_legs.load_a_to_load_b)} mi deadhead`
                                                            : `${formatNumber(selectedChainDetail.deadhead_legs.load_b_to_load_c)} mi deadhead`
                                                        }
                                                    </div>
                                                )}
                                                <div className="flex items-center gap-4 bg-zinc-800/50 border border-zinc-700 rounded-sm p-3">
                                                    <div className="w-8 h-8 bg-orange-500/20 rounded-full flex items-center justify-center shrink-0">
                                                        <span className="text-orange-400 font-bold text-sm">
                                                            {String.fromCharCode(65 + idx)}
                                                        </span>
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 text-white">
                                                            <span className="truncate">{load.origin}</span>
                                                            <ArrowRight className="w-4 h-4 text-zinc-500 shrink-0" />
                                                            <span className="truncate">{load.destination}</span>
                                                        </div>
                                                        <div className="flex items-center gap-4 mt-1 text-xs text-zinc-500">
                                                            <span>{load.distance} mi</span>
                                                            <span>Pickup: {load.pickup_date}</span>
                                                        </div>
                                                    </div>
                                                    <div className="text-right shrink-0">
                                                        <p className="text-green-400 font-mono font-semibold">
                                                            {formatCurrency(load.gross_pay)}
                                                        </p>
                                                        <p className="text-xs text-zinc-500">
                                                            ${formatNumber(load.gross_pay / load.distance, 2)}/mi
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="bg-zinc-800/30 border border-zinc-700 rounded-sm p-4">
                                        <h4 className="text-sm font-medium text-zinc-300 mb-3 uppercase tracking-wide">
                                            Distance Breakdown
                                        </h4>
                                        <div className="space-y-2 text-sm">
                                            <div className="flex justify-between">
                                                <span className="text-zinc-400">Loaded Miles</span>
                                                <span className="text-white font-mono">{formatNumber(selectedChainDetail.total_loaded_miles, 0)} mi</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-zinc-400">Deadhead Miles</span>
                                                <span className="text-yellow-400 font-mono">{formatNumber(selectedChainDetail.total_deadhead_miles, 0)} mi</span>
                                            </div>
                                            <div className="flex justify-between pt-2 border-t border-zinc-700">
                                                <span className="text-zinc-400">Total Miles</span>
                                                <span className="text-white font-mono font-semibold">
                                                    {formatNumber(selectedChainDetail.total_loaded_miles + selectedChainDetail.total_deadhead_miles, 0)} mi
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-zinc-800/30 border border-zinc-700 rounded-sm p-4">
                                        <h4 className="text-sm font-medium text-zinc-300 mb-3 uppercase tracking-wide">
                                            Financial Summary
                                        </h4>
                                        <div className="space-y-2 text-sm">
                                            <div className="flex justify-between">
                                                <span className="text-zinc-400">Revenue/Day</span>
                                                <span className="text-green-400 font-mono">{formatCurrency(selectedChainDetail.revenue_per_day)}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-zinc-400">Fuel Cost (est)</span>
                                                <span className="text-red-400 font-mono">-{formatCurrency(selectedChainDetail.total_deadhead_miles * 0.5)}</span>
                                            </div>
                                            <div className="flex justify-between pt-2 border-t border-zinc-700">
                                                <span className="text-zinc-400">Heat Bonus</span>
                                                <span className="text-orange-400 font-mono">+{selectedChainDetail.heat_bonus || 0}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Stage Chain Section */}
                                {!stageResult ? (
                                    <div className="bg-zinc-800/30 border border-orange-500/30 rounded-sm p-4 mt-4">
                                        <div className="flex items-start gap-3">
                                            <div className="w-10 h-10 bg-orange-500/20 rounded-sm flex items-center justify-center shrink-0 mt-0.5">
                                                <Route className="w-5 h-5 text-orange-500" />
                                            </div>
                                            <div className="flex-1">
                                                <h4 className="text-sm font-medium text-white mb-1">
                                                    Stage This Chain
                                                </h4>
                                                <p className="text-xs text-zinc-400 mb-3">
                                                    Assign all 3 loads to the truck with "Pending Confirmation" status. 
                                                    You will need to confirm each load individually after broker confirmation.
                                                </p>
                                                <div className="flex items-center gap-2">
                                                    <Button
                                                        onClick={() => handleStageChain(selectedChainDetail)}
                                                        disabled={stagingChain}
                                                        className="bg-orange-500 hover:bg-orange-600 text-white"
                                                        data-testid="stage-chain-btn"
                                                    >
                                                        {stagingChain ? (
                                                            <>
                                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                                Staging...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Route className="w-4 h-4 mr-2" />
                                                                Stage Chain
                                                            </>
                                                        )}
                                                    </Button>
                                                    <span className="text-xs text-zinc-500">
                                                        This does NOT book the loads automatically
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : stageResult.error ? (
                                    <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-4 mt-4">
                                        <div className="flex items-center gap-2 text-red-400">
                                            <AlertTriangle className="w-5 h-5" />
                                            <span className="font-medium">Staging Failed</span>
                                        </div>
                                        <p className="text-sm text-red-400/80 mt-2">{stageResult.error}</p>
                                        <Button 
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setStageResult(null)}
                                            className="mt-3 border-red-500/30 text-red-400 hover:bg-red-500/10"
                                        >
                                            Try Again
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="bg-green-500/10 border border-green-500/30 rounded-sm p-4 mt-4">
                                        <div className="flex items-center gap-2 text-green-400 mb-3">
                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                            <span className="font-medium">Chain Staged Successfully</span>
                                        </div>
                                        
                                        <div className="space-y-2 text-sm">
                                            <div className="flex items-center gap-2">
                                                <TruckIcon className="w-4 h-4 text-zinc-400" />
                                                <span className="text-zinc-400">Assigned to:</span>
                                                <span className="text-white font-medium">{stageResult.truck_name}</span>
                                            </div>
                                            
                                            <div className="flex items-center gap-2">
                                                <span className="text-zinc-400">Staged loads:</span>
                                                <span className="text-green-400 font-mono">{stageResult.staged_loads?.length || 0}</span>
                                            </div>
                                            
                                            {stageResult.failed_loads?.length > 0 && (
                                                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded p-2 mt-2">
                                                    <p className="text-yellow-400 text-xs font-medium mb-1">
                                                        {stageResult.failed_loads.length} load(s) could not be staged:
                                                    </p>
                                                    {stageResult.failed_loads.map((fail, idx) => (
                                                        <p key={idx} className="text-yellow-400/80 text-xs">
                                                            • {fail.load_id}: {fail.reason}
                                                        </p>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        <div className="mt-4 pt-3 border-t border-green-500/20">
                                            <p className="text-xs text-zinc-400 mb-2">
                                                <strong className="text-zinc-300">Next Steps:</strong>
                                            </p>
                                            <ol className="text-xs text-zinc-400 list-decimal list-inside space-y-1">
                                                <li>Contact brokers to confirm each load</li>
                                                <li>Go to <span className="text-white">My Loads</span> page to view staged loads</li>
                                                <li>Confirm each load individually after broker confirmation</li>
                                            </ol>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            // Chain List View
                            <div className="space-y-3" data-testid="chains-list">
                                {generatedChains.map((chain, index) => (
                                    <div
                                        key={chain.chain_id}
                                        className={`bg-zinc-800/30 border rounded-sm p-4 cursor-pointer transition-colors ${
                                            chain.high_risk_broker 
                                                ? 'border-red-500/50 hover:border-red-500/70' 
                                                : 'border-zinc-700 hover:border-orange-500/50'
                                        }`}
                                        onClick={() => {
                                            setSelectedChainDetail(chain);
                                            setStageResult(null);
                                        }}
                                        data-testid={`chain-item-${index}`}
                                    >
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center gap-2">
                                                <Badge className="bg-orange-500/20 text-orange-400 border-0">
                                                    #{index + 1}
                                                </Badge>
                                                <span className="text-xs text-zinc-500">
                                                    Score: <span className="text-orange-400 font-mono font-semibold">{formatNumber(chain.chain_score, 0)}</span>
                                                </span>
                                                {chain.high_risk_broker && (
                                                    <Badge className="bg-red-500/20 text-red-400 border-0" data-testid="high-risk-badge">
                                                        <AlertTriangle className="w-3 h-3 mr-1" />
                                                        High Risk
                                                    </Badge>
                                                )}
                                            </div>
                                            <ChevronRight className="w-5 h-5 text-zinc-500" />
                                        </div>

                                        {/* Load Sequence */}
                                        <div className="flex items-center gap-2 mb-3 text-sm">
                                            {chain.loads.map((load, loadIdx) => (
                                                <div key={load.load_id} className="flex items-center gap-2">
                                                    {loadIdx > 0 && <ArrowRight className="w-3 h-3 text-zinc-600" />}
                                                    <span className="text-white">
                                                        {load.origin.split(',')[0]}
                                                    </span>
                                                </div>
                                            ))}
                                            <ArrowRight className="w-3 h-3 text-zinc-600" />
                                            <span className="text-white">
                                                {chain.loads[chain.loads.length - 1].destination.split(',')[0]}
                                            </span>
                                        </div>

                                        {/* Metrics Grid */}
                                        <div className="grid grid-cols-5 gap-4 text-xs">
                                            <div>
                                                <p className="text-zinc-500 uppercase">Revenue</p>
                                                <p className="text-green-400 font-mono font-semibold mt-0.5">
                                                    {formatCurrency(chain.total_revenue)}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-zinc-500 uppercase">Est. Days</p>
                                                <p className="text-white font-mono mt-0.5">
                                                    {formatNumber(chain.estimated_total_days)}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-zinc-500 uppercase">Rev/Day</p>
                                                <p className="text-green-400 font-mono font-semibold mt-0.5">
                                                    {formatCurrency(chain.revenue_per_day)}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-zinc-500 uppercase">Deadhead</p>
                                                <p className="text-yellow-400 font-mono mt-0.5">
                                                    {formatNumber(chain.total_deadhead_miles, 0)} mi
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-zinc-500 uppercase">Loaded</p>
                                                <p className="text-white font-mono mt-0.5">
                                                    {formatNumber(chain.total_loaded_miles, 0)} mi
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <DialogFooter className="shrink-0 border-t border-zinc-800 pt-4">
                        <Button 
                            variant="outline" 
                            onClick={() => {
                                setShowChainsDialog(false);
                                setSelectedChainDetail(null);
                            }}
                            className="border-zinc-700"
                        >
                            Close
                        </Button>
                        {generatedChains.length > 0 && !selectedChainDetail && (
                            <Button 
                                onClick={() => handleGenerateChains(selectedTruckForChains)}
                                disabled={generatingChains}
                                className="bg-orange-500 hover:bg-orange-600 text-white"
                            >
                                <RefreshCw className={`w-4 h-4 mr-2 ${generatingChains ? 'animate-spin' : ''}`} />
                                Regenerate
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
